// Signal-backed store for the user's favorite projects/tasks and the
// "recently viewed" list. Both are loaded lazily on first use, refreshed on
// demand, and updated optimistically on mutation. No polling — the server
// data only changes when *this* tab triggers it.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { FavoriteEntityType } from "@juuplan/shared";
import { api, ApiError } from "../lib/api";
import { toastError } from "../lib/toast";

// ---------- favorites ----------

export interface FavoritesState {
  project: Set<number>;
  task: Set<number>;
  personal_task: Set<number>;
  list: Set<number>;
  loading: boolean;
}

const favsInitial: FavoritesState = {
  project: new Set<number>(),
  task: new Set<number>(),
  personal_task: new Set<number>(),
  list: new Set<number>(),
  loading: true,
};
const favsSignal: Signal<FavoritesState> = signal(favsInitial);
let favsInFlight: Promise<void> | null = null;
let favsStarted = false;

interface FavoritesResponse {
  project: number[];
  task: number[];
  personal_task: number[];
  list: number[];
}

async function loadFavorites(): Promise<void> {
  try {
    const data = await api<FavoritesResponse>("/api/me/favorites", {
      silent401: true,
      silentError: true,
    });
    favsSignal.value = {
      project: new Set(data.project ?? []),
      task: new Set(data.task ?? []),
      personal_task: new Set(data.personal_task ?? []),
      list: new Set(data.list ?? []),
      loading: false,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    favsSignal.value = { ...favsSignal.value, loading: false };
  } finally {
    favsInFlight = null;
  }
}

export function useFavorites(): FavoritesState {
  useEffect(() => {
    if (!favsStarted) {
      favsStarted = true;
      favsInFlight = loadFavorites();
    }
  }, []);
  return favsSignal.value;
}

export function refreshFavorites(): Promise<void> {
  favsInFlight = loadFavorites();
  return favsInFlight;
}

export function isFavorite(
  entityType: FavoriteEntityType,
  id: number
): boolean {
  return favsSignal.value[entityType].has(id);
}

export async function toggleFavorite(
  entityType: FavoriteEntityType,
  id: number
): Promise<void> {
  const current = favsSignal.value;
  const bucket = current[entityType];
  const wasFavorite = bucket.has(id);

  // Optimistic update.
  const nextBucket = new Set(bucket);
  if (wasFavorite) {
    nextBucket.delete(id);
  } else {
    nextBucket.add(id);
  }
  favsSignal.value = { ...current, [entityType]: nextBucket };

  try {
    if (wasFavorite) {
      await api(`/api/me/favorites/${entityType}/${id}`, {
        method: "DELETE",
        silentError: true,
      });
    } else {
      await api("/api/me/favorites", {
        method: "POST",
        body: { entity_type: entityType, entity_id: id },
        silentError: true,
      });
    }
  } catch (err) {
    // Roll back ONLY this id in this bucket — restoring the full snapshot
    // would clobber any other toggle that succeeded while this one was
    // in flight.
    const latest = favsSignal.value;
    const rolledBack = new Set(latest[entityType]);
    if (wasFavorite) rolledBack.add(id);
    else rolledBack.delete(id);
    favsSignal.value = { ...latest, [entityType]: rolledBack };

    // Die Meldung gehoert HIERHER, nicht zu den Aufrufern (Audit 2026-09-04).
    // Beide Aufrufe laufen mit `silentError: true`, `api()` meldet also nichts
    // — und vier der fuenf Aufrufstellen rufen `void toggleFavorite(...)` ohne
    // jede Behandlung. Ein Fehlschlag klappte das Herz-Symbol damit still
    // zurueck: der Nutzer sieht seine Aktion sich selbst rueckgaengig machen
    // und erfaehrt nicht, warum. Genau EINE Stelle formuliert die Meldung,
    // damit die Aufrufer sie nicht verdoppeln.
    toastError(
      wasFavorite ? "Favorit entfernen fehlgeschlagen." : "Favorit setzen fehlgeschlagen."
    );
    throw err;
  }
}

// ---------- detailed favorites (global Favoriten page) ----------

export interface FavoriteDetail {
  entity_type: FavoriteEntityType;
  entity_id: number;
  title: string;
  subtitle?: string;
  link: string;
  color?: string | null;
}

/** Fetch the resolved (name + link) list of every favorite the user can access. */
export async function fetchFavoritesDetailed(): Promise<FavoriteDetail[]> {
  return api<FavoriteDetail[]>("/api/me/favorites/detailed", {
    silent401: true,
  });
}

// ---------- recents ----------

export interface RecentItem {
  entity_type: FavoriteEntityType;
  entity_id: number;
  viewed_at: string;
  name: string;
  project_id?: number;
  color?: string | null;
}

export interface RecentsState {
  items: RecentItem[];
  loading: boolean;
}

const recentsInitial: RecentsState = { items: [], loading: true };
const recentsSignal: Signal<RecentsState> = signal(recentsInitial);
let recentsInFlight: Promise<void> | null = null;
let recentsStarted = false;

async function loadRecents(): Promise<void> {
  try {
    const items = await api<RecentItem[]>("/api/me/recents?limit=10", {
      silent401: true,
      silentError: true,
    });
    recentsSignal.value = { items: items ?? [], loading: false };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    recentsSignal.value = { ...recentsSignal.value, loading: false };
  } finally {
    recentsInFlight = null;
  }
}

export function useRecents(): RecentsState {
  useEffect(() => {
    if (!recentsStarted) {
      recentsStarted = true;
      recentsInFlight = loadRecents();
    }
  }, []);
  return recentsSignal.value;
}

export function refreshRecents(): Promise<void> {
  recentsInFlight = loadRecents();
  return recentsInFlight;
}

/**
 * Track a recent view. Fire-and-forget — never block the UI; just refresh
 * the list once the server has updated. Multiple rapid calls for the same
 * entity in one session are de-duped via a short in-memory cache.
 */
const recentlyTracked = new Map<string, number>();
const TRACK_DEDUP_MS = 30_000; // don't ping the server more than once per 30s per entity

export async function trackRecent(
  entityType: FavoriteEntityType,
  id: number
): Promise<void> {
  const key = `${entityType}:${id}`;
  const now = Date.now();
  const last = recentlyTracked.get(key);
  if (last !== undefined && now - last < TRACK_DEDUP_MS) return;
  recentlyTracked.set(key, now);

  try {
    await api("/api/me/recents/track", {
      method: "POST",
      body: { entity_type: entityType, entity_id: id },
      silentError: true,
      silent401: true,
    });
    // Refresh the visible list so Home reflects the new top item.
    void refreshRecents();
  } catch {
    // Silent — tracking is best-effort.
  }
}
